import { type AnswerData, type Question } from "@repo/api";
import { Checkbox } from "@repo/skapa";
import { translateQuestion } from "@repo/ui";

import { useAnswers } from "./AnswersProvider";
import { ChecklistQuestion } from "./questions/ChecklistQuestion";
import { ColorQuestion } from "./questions/ColorQuestion";
import { DatePickerQuestion } from "./questions/DatePickerQuestion";
import { DropdownQuestion } from "./questions/DropdownQuestion";
import { FreeTextQuestion } from "./questions/FreeTextQuestion";
import { MultiSelectQuestion } from "./questions/MultiSelectQuestion";
import { NumberQuestion } from "./questions/NumberQuestion";
import { ShapeQuestion } from "./questions/ShapeQuestion";
import { SingleSelectQuestion } from "./questions/SingleSelectQuestion";
import { TextAreaQuestion } from "./questions/TextAreaQuestion";
import { YesorNoQuestion } from "./questions/YesorNoQuestions";

interface QuestionItemProps {
  question: Question;
  isB2B?: boolean;
}
export const QuestionItem = ({
  question,
  isB2B = false,
}: QuestionItemProps) => {
  const { answers, updateAnswers, b2bAnswers, updateB2bAnswers } = useAnswers();
  const targetAnswers = isB2B ? b2bAnswers : answers;
  const updateTargetAnswers = isB2B ? updateB2bAnswers : updateAnswers;

  const filteredAnswer = targetAnswers?.find(
    (answer: AnswerData) => answer.questionId === question.question_id_key
  );

  const setAnswerFreeText = (localSelected: string | undefined) => {
    const questionId = question.question_id_key;
    const existingQuestionIndex = targetAnswers.findIndex(
      (answer: AnswerData) => answer.questionId === questionId
    );

    const updatedAnswers =
      existingQuestionIndex === -1
        ? [
            ...targetAnswers,
            {
              questionId,
              answerIds: [],
              answerFreeText: localSelected,
              type: "NEED_ANALYSIS",
              questionText: question.question_name,
              answerTexts: [],
            },
          ]
        : targetAnswers.map((answer: AnswerData) =>
            answer.questionId === questionId
              ? { ...answer, answerFreeText: localSelected }
              : answer
          );

    updateTargetAnswers(updatedAnswers);
  };

  const setAnswerIds = (localSelected: string[]) => {
    const questionId = question.question_id_key;

    const selectedAnswerTexts =
      question.answers
        ?.filter((answer) => localSelected.includes(answer.answer_id_key))
        .map((answer) => answer.answer_name) ?? [];

    const existingQuestionIndex = targetAnswers.findIndex(
      (answer: AnswerData) => answer.questionId === questionId
    );

    const updatedAnswers =
      existingQuestionIndex === -1
        ? [
            ...targetAnswers,
            {
              questionId,
              answerIds: localSelected,
              answerFreeText: "",
              type: "NEED_ANALYSIS",
              questionText: question.question_name,
              answerTexts: selectedAnswerTexts,
            },
          ]
        : targetAnswers.map((answer: AnswerData) =>
            answer.questionId === questionId
              ? {
                  ...answer,
                  answerIds: localSelected,
                  answerTexts: selectedAnswerTexts,
                }
              : answer
          );
    updateTargetAnswers(updatedAnswers);
  };

  switch (question.kind) {
    case "FREE_TEXT":
      return (
        <FreeTextQuestion
          answer={filteredAnswer}
          onFreeTextAnswered={setAnswerFreeText}
        />
      );
    case "TEXTAREA":
      return (
        <TextAreaQuestion
          answer={filteredAnswer}
          onFreeTextAnswered={setAnswerFreeText}
        />
      );
    case "NUMBER":
      return (
        <NumberQuestion
          answer={filteredAnswer}
          onFreeTextAnswered={setAnswerFreeText}
        />
      );
    case "DATE_PICKER":
      return (
        <DatePickerQuestion
          answer={filteredAnswer}
          onFreeTextAnswered={setAnswerFreeText}
        />
      );

    case "SINGLE_SELECT":
      return (
        <SingleSelectQuestion
          answer={filteredAnswer}
          onAnswerIdAnswered={setAnswerIds}
          question={question}
        />
      );

    case "MULTI_SELECT":
      return (
        <MultiSelectQuestion
          answer={filteredAnswer}
          onAnswerIdAnswered={setAnswerIds}
          question={question}
          onFreeTextAnswered={setAnswerFreeText}
        />
      );
    case "DROPDOWN":
      return (
        <DropdownQuestion
          answer={filteredAnswer}
          onAnswerIdAnswered={setAnswerIds}
          question={question}
        />
      );

    case "CHECKLIST":
      return (
        <ChecklistQuestion
          answer={filteredAnswer}
          onAnswerIdAnswered={setAnswerIds}
          question={question}
          onFreeTextAnswered={setAnswerFreeText}
        />
      );
    case "SHAPE":
      return (
        <ShapeQuestion
          answer={filteredAnswer}
          onAnswerIdAnswered={setAnswerIds}
          question={question}
          onFreeTextAnswered={setAnswerFreeText}
        />
      );

    case "COLOR":
      return (
        <ColorQuestion
          answer={filteredAnswer}
          onAnswerIdAnswered={setAnswerIds}
          question={question}
        />
      );

    case "CHECKBOX":
      return (
        <Checkbox
          subtle
          id={question.question_id_key}
          label={translateQuestion(question.question_name)}
          value={question.question_id_key}
        />
      );
    case "YESORNOQUESTION":
      return (
        <YesorNoQuestion
          answer={filteredAnswer}
          onAnswerIdAnswered={setAnswerIds}
          question={question}
          onFreeTextAnswered={setAnswerFreeText}
        />
      );
    default:
      return null;
  }
};
